import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import AppearanceTab from "./AppearanceTab";
import SpecialThemesTab from "./SpecialThemesTab";
import NotificationsTab from "./NotificationsTab";
import AccountTab from "./AccountTab";
import { updateSettings } from "../../api/api";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { THEME_SYSTEM } from "../utils";
import {
  normalizeOrgSettings,
  normalizeUserSettings,
  settingsFromResponse,
} from "../../lib/accountSettings";
import SignedOutPrompt from "../SignedOutPrompt";

/**
 * User settings: account, appearance, and (for people) notification emails.
 * Theme and notification changes persist immediately; there is no save button.
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {Object} [props.user]
 * @param {string} [props.theme]
 * @param {function(string): void} [props.setTheme]
 * @param {function(Object): void} [props.onSettingsSaved]
 * @param {function(Object): void} [props.onSetUser]
 * @param {function(): Promise<string>} [props.onAccountRemoved]
 * @returns {JSX.Element}
 */
function SettingsPage({ userId, user, theme, setTheme, onSettingsSaved, onSetUser, onAccountRemoved }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const isOrg = user?.type === "organization";
  const accountType = isOrg ? "organization" : "user";

  const [settings, setSettings] = useState(() =>
    isOrg ? normalizeOrgSettings(user?.settings) : normalizeUserSettings(user?.settings)
  );
  const [saveError, setSaveError] = useState("");
  const saveGenerationRef = useRef(0);

  useEffect(() => {
    setSettings(
      isOrg ? normalizeOrgSettings(user?.settings) : normalizeUserSettings(user?.settings)
    );
  }, [user?.settings, isOrg]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      theme: theme || THEME_SYSTEM,
    }));
  }, [theme]);

  /**
   * Persist a partial settings patch immediately.
   * @param {Object} patch
   * @returns {Promise<void>}
   */
  const persistPatch = async (patch) => {
    if (!userId) return;
    const generation = saveGenerationRef.current + 1;
    saveGenerationRef.current = generation;
    setSaveError("");
    const result = await updateSettings(userId, patch, { accountType });
    if (generation !== saveGenerationRef.current) {
      return;
    }
    if (result.error) {
      setSaveError(result.error);
      setSettings(
        isOrg ? normalizeOrgSettings(user?.settings) : normalizeUserSettings(user?.settings)
      );
      if (patch.theme) {
        setTheme(user?.settings?.theme || THEME_SYSTEM);
      }
      return;
    }
    const saved = settingsFromResponse(result.data);
    const next = isOrg ? normalizeOrgSettings(saved) : normalizeUserSettings(saved);
    setSettings(next);
    if (onSettingsSaved) {
      onSettingsSaved(next);
    }
  };

  const handleUpdateSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (patch.theme) {
      setTheme(patch.theme);
    }
    await persistPatch(patch);
  };

  const handleUpdateNestedSettings = async (category, updates) => {
    const nextCategory = { ...settings[category], ...updates };
    setSettings({ ...settings, [category]: nextCategory });
    await persistPatch({ [category]: updates });
  };

  const tabs = isOrg
    ? [
        { path: "account", label: "Account" },
        { path: "appearance", label: "Appearance" },
      ]
    : [
        { path: "account", label: "Account" },
        { path: "appearance", label: "Appearance" },
        { path: "notifications", label: "Notifications" },
      ];

  const isActiveTab = (path) => {
    if (path === "account") {
      return location.pathname === "/settings" || location.pathname === "/settings/account";
    }
    if (path === "appearance") {
      return (
        location.pathname === "/settings/appearance" ||
        location.pathname.startsWith("/settings/appearance/")
      );
    }
    return location.pathname === `/settings/${path}`;
  };

  const pageEnter = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] },
      };

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Settings"
        description="Sign in to view and change your account, appearance, notifications, and preferences."
      />
    );
  }

  return (
    <div className="bg-ui-background w-full min-h-0">
      <motion.div className="mx-auto max-w-7xl px-4 py-6 sm:py-8" {...pageEnter}>
        <h1 className="mb-6 text-2xl font-bold tracking-tight text-text-primary sm:mb-8 sm:text-3xl">Settings</h1>

        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <aside className="w-full shrink-0 lg:w-60">
            <Card className="overflow-hidden p-1 shadow-profile-sm">
              <nav className="flex flex-col gap-0.5" aria-label="Settings sections">
                {tabs.map((tab) => {
                  const active = isActiveTab(tab.path);
                  return (
                    <Button
                      key={tab.path}
                      asChild
                      variant={active ? "default" : "ghost"}
                      className={cn("h-auto w-full justify-start px-3 py-2.5 text-left font-normal", !active && "text-text-secondary")}
                    >
                      <Link to={`/settings/${tab.path}`}>{tab.label}</Link>
                    </Button>
                  );
                })}
              </nav>
            </Card>
          </aside>

          <div className="min-w-0 flex-1">
            <Card className="min-h-[12rem] shadow-profile-sm">
              <CardContent className="p-4 sm:p-6">
                {saveError ? (
                  <p className="mb-4 text-sm text-error" role="alert">
                    Could not save settings: {saveError}
                  </p>
                ) : null}
                <Routes>
                  <Route path="/" element={<Navigate to="/settings/account" replace />} />
                  <Route
                    path="account"
                    element={
                      <AccountTab
                        userId={userId}
                        user={user}
                        onAccountUpdated={onSetUser}
                        onAccountRemoved={onAccountRemoved}
                      />
                    }
                  />
                  <Route
                    path="appearance"
                    element={
                      <AppearanceTab
                        theme={settings.theme}
                        setTheme={setTheme}
                        onUpdateSettings={handleUpdateSettings}
                      />
                    }
                  />
                  <Route
                    path="appearance/special"
                    element={
                      <SpecialThemesTab
                        theme={settings.theme}
                        setTheme={setTheme}
                        onUpdateSettings={handleUpdateSettings}
                      />
                    }
                  />
                  {isOrg ? (
                    <Route path="notifications" element={<Navigate to="/settings/appearance" replace />} />
                  ) : (
                    <Route
                      path="notifications"
                      element={
                        <NotificationsTab
                          notifications={settings.notifications}
                          onUpdateSettings={handleUpdateNestedSettings}
                        />
                      }
                    />
                  )}
                </Routes>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default SettingsPage;
